/**
 * SystemParametersSection — Issue #267.
 *
 * Renders the system parameter center inside the `/settings` page:
 *   - The non-secret catalog (key, name, risk, scope, current published value,
 *     default value, allowed range, override policy).
 *   - Pending change requests for review.
 *   - A change-request form (writable only when the caller holds
 *     `system_parameters:write` or `*:*`).
 *   - Approve / reject buttons for approvers (gated by
 *     `system_parameters:approve` or `*:*`).
 *
 * 红线：
 * - 严禁渲染任何 secret / token / cookie / 生产 env / provider 凭据。Catalog
 *   只展示后端返回的非敏感业务参数；高风险/锁定参数仍只展示元数据，不允许
 *   绕开后端校验。
 * - 表单提交必须使用 typed `useSystemParameterCenter`；前端不伪装成功。
 * - 高风险审批的二次验证证据是 service 端持久化的 challenge id；UI 只让操作
 *   人粘贴/输入后端签发的 challenge id，绝不自动填 `passed` 之类的伪证据。
 */
import { useMemo, useState, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useSystemParameterCenter,
  type SystemParameterChangeDto,
  type SystemParameterDto,
} from '@/hooks/useSystemParameters';

export const SP_WRITE_PERMISSION = 'system_parameters:write';
export const SP_APPROVE_PERMISSION = 'system_parameters:approve';
const SUPER_ADMIN = '*:*';

export function canWriteSystemParameters(permissions: readonly string[]): boolean {
  return permissions.includes(SUPER_ADMIN) || permissions.includes(SP_WRITE_PERMISSION);
}

export function canApproveSystemParameters(permissions: readonly string[]): boolean {
  return permissions.includes(SUPER_ADMIN) || permissions.includes(SP_APPROVE_PERMISSION);
}

const changeSchema = z.object({
  key: z.string().min(3, '请选择参数 key'),
  newValueText: z.string().min(1, '新值必填'),
  reason: z.string().min(4, '原因至少 4 个字符'),
  approvalNo: z
    .string()
    .optional()
    .refine(
      (v) => !v || /^[A-Za-z0-9_-]{4,120}$/.test(v),
      '审批单号必须为 4..120 位 [A-Za-z0-9_-]',
    ),
  impactScope: z.string().optional(),
  rollbackPlan: z.string().optional(),
  gradualRollout: z.string().optional(),
});

type ChangeFormValues = z.infer<typeof changeSchema>;

export interface SystemParametersSectionProps {
  readonly permissions: readonly string[];
}

export default function SystemParametersSection({
  permissions,
}: SystemParametersSectionProps): ReactElement {
  const center = useSystemParameterCenter();
  const canWrite = canWriteSystemParameters(permissions);
  const canApprove = canApproveSystemParameters(permissions);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);

  const form = useForm<ChangeFormValues>({
    resolver: zodResolver(changeSchema),
    defaultValues: {
      key: '',
      newValueText: '',
      reason: '',
      approvalNo: '',
      impactScope: '',
      rollbackPlan: '',
      gradualRollout: '',
    },
  });

  const selectedKey = form.watch('key');
  const selectedParam = useMemo<SystemParameterDto | undefined>(() => {
    return center.catalog.data?.find((row) => row.key === selectedKey);
  }, [center.catalog.data, selectedKey]);

  const overrideParams = useMemo<readonly SystemParameterDto[]>(() => {
    return center.catalog.data?.filter((row) => row.allowOrgOverride) ?? [];
  }, [center.catalog.data]);

  async function onSubmit(values: ChangeFormValues): Promise<void> {
    setFormError(null);
    setSubmitMessage(null);
    let parsed: JsonValue;
    try {
      parsed = parseTextValue(values.newValueText, selectedParam);
    } catch (err) {
      setFormError((err as Error).message);
      return;
    }
    if (selectedParam?.riskLevel === 'high') {
      if (!values.approvalNo?.trim()) {
        setFormError('高风险参数必须填写合规审批单号。');
        return;
      }
      if (!values.gradualRollout?.trim()) {
        setFormError('高风险参数必须填写灰度范围。');
        return;
      }
      if (!values.impactScope?.trim()) {
        setFormError('高风险参数必须填写影响范围。');
        return;
      }
      if (!values.rollbackPlan?.trim()) {
        setFormError('高风险参数必须填写回滚方案。');
        return;
      }
    }
    try {
      await center.createChange({
        key: values.key,
        newValue: parsed,
        reason: values.reason,
        approvalNo: values.approvalNo?.trim() || undefined,
        impactScope: values.impactScope?.trim() || undefined,
        rollbackPlan: values.rollbackPlan?.trim() || undefined,
        gradualRollout: values.gradualRollout?.trim() || undefined,
      });
      setSubmitMessage(`已提交参数 ${values.key} 的变更申请。`);
      form.reset({
        key: values.key,
        newValueText: '',
        reason: '',
        approvalNo: '',
        impactScope: '',
        rollbackPlan: '',
        gradualRollout: '',
      });
    } catch (err) {
      setFormError((err as Error).message);
    }
  }

  return (
    <section
      className="space-y-3"
      data-testid="settings-system-parameters-section"
      aria-label="系统参数中心"
    >
      <div>
        <h3 className="text-lg font-medium">系统参数中心</h3>
        <p className="text-sm text-muted-foreground">
          后端真实参数目录、当前发布值、风险等级与机构覆盖策略。普通管理员只读；超级管理员或
          模块负责人可创建变更申请，审批者可发布或拒绝。系统参数不存储任何密钥、token、cookie、
          生产 env 或 provider 凭据。
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">参数目录</CardTitle>
          <CardDescription>
            来自 GET `/api/v1/settings/system-parameters/catalog`。
            {!center.catalog.loading && (
              <span data-testid="settings-sp-count">
                {' '}
                共 {center.catalog.data?.length ?? 0} 项
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {center.catalog.loading && (
            <div className="text-sm text-muted-foreground" data-testid="settings-sp-loading">
              正在加载…
            </div>
          )}
          {center.catalog.error && (
            <div
              className="text-sm text-destructive"
              data-testid="settings-sp-catalog-error"
              role="alert"
            >
              {center.catalog.error}
            </div>
          )}
          {center.catalog.data && center.catalog.data.length === 0 && !center.catalog.loading && (
            <div className="text-sm text-muted-foreground" data-testid="settings-sp-empty">
              后端尚未为当前 tenant/org 初始化系统参数。
            </div>
          )}
          {center.catalog.data && center.catalog.data.length > 0 && (
            <div className="overflow-x-auto">
              <table
                className="min-w-full divide-y border text-sm"
                data-testid="settings-sp-table"
              >
                <thead className="bg-muted/40">
                  <tr>
                    <th className="px-2 py-2 text-left font-medium">Key</th>
                    <th className="px-2 py-2 text-left font-medium">名称</th>
                    <th className="px-2 py-2 text-left font-medium">当前值</th>
                    <th className="px-2 py-2 text-left font-medium">默认值</th>
                    <th className="px-2 py-2 text-left font-medium">范围</th>
                    <th className="px-2 py-2 text-left font-medium">风险</th>
                    <th className="px-2 py-2 text-left font-medium">机构覆盖</th>
                    <th className="px-2 py-2 text-left font-medium">锁定</th>
                    <th className="px-2 py-2 text-left font-medium">版本</th>
                  </tr>
                </thead>
                <tbody>
                  {center.catalog.data.map((row) => (
                    <tr
                      key={row.id}
                      data-testid={`settings-sp-row-${row.key}`}
                      className="border-t"
                    >
                      <td className="px-2 py-2 font-mono text-xs">{row.key}</td>
                      <td className="px-2 py-2">{row.name}</td>
                      <td className="px-2 py-2 font-mono text-xs">
                        {formatJsonValue(row.currentValue)}
                      </td>
                      <td className="px-2 py-2 font-mono text-xs">
                        {formatJsonValue(row.defaultValue)}
                      </td>
                      <td className="px-2 py-2 font-mono text-xs">
                        {formatJsonValue(row.allowedRange ?? null)}
                      </td>
                      <td className="px-2 py-2">{row.riskLevel}</td>
                      <td className="px-2 py-2">
                        {row.allowOrgOverride ? '允许' : '禁止'}
                      </td>
                      <td className="px-2 py-2">
                        {row.locked ? (
                          <span
                            className="rounded bg-muted px-1 py-0.5 text-xs"
                            data-testid={`settings-sp-locked-${row.key}`}
                          >
                            锁定
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-2 py-2">{row.version}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {overrideParams.length > 0 && (
            <p className="mt-3 text-xs text-muted-foreground" data-testid="settings-sp-override-list">
              允许机构覆盖：
              {overrideParams.map((p) => p.key).join('、')}
            </p>
          )}
        </CardContent>
      </Card>

      {canWrite && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">创建变更申请</CardTitle>
            <CardDescription>
              超级管理员 / 模块负责人可发起。高风险参数必须填审批单号、灰度范围、影响范围与
              回滚方案，并由双人复核 + 服务端校验的二次验证 challenge 才能发布。
              点击表单中的参数后会展示其类型、范围与默认值。
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="space-y-3"
              data-testid="settings-sp-create-form"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <div>
                <Label htmlFor="sp-key">参数 key</Label>
                <select
                  id="sp-key"
                  data-testid="settings-sp-key-select"
                  className="block w-full rounded border bg-background px-2 py-2 text-sm"
                  {...form.register('key')}
                >
                  <option value="">请选择参数</option>
                  {center.catalog.data
                    ?.filter((row) => !row.locked)
                    .map((row) => (
                      <option key={row.id} value={row.key}>
                        {row.key}（{row.name}）
                      </option>
                    ))}
                </select>
                {form.formState.errors.key && (
                  <p className="text-xs text-destructive">{form.formState.errors.key.message}</p>
                )}
              </div>
              {selectedParam && (
                <div
                  className="rounded border bg-muted/40 p-2 text-xs"
                  data-testid="settings-sp-selected-info"
                >
                  <div>
                    类型：<code>{selectedParam.valueType}</code>；风险：
                    <strong>{selectedParam.riskLevel}</strong>；
                    范围：<code>{formatJsonValue(selectedParam.allowedRange ?? null)}</code>
                  </div>
                  <div>
                    当前发布值：<code>{formatJsonValue(selectedParam.currentValue)}</code>
                  </div>
                  {selectedParam.description && (
                    <div className="mt-1 text-muted-foreground">{selectedParam.description}</div>
                  )}
                </div>
              )}
              <div>
                <Label htmlFor="sp-new-value">目标值（JSON）</Label>
                <Input
                  id="sp-new-value"
                  data-testid="settings-sp-new-value"
                  autoComplete="off"
                  {...form.register('newValueText')}
                />
                <p className="text-xs text-muted-foreground">
                  布尔值填 true/false；数字直接填；字符串可填裸字符串或 JSON 字符串；JSON 填合法 JSON。
                </p>
                {form.formState.errors.newValueText && (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.newValueText.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="sp-reason">变更原因</Label>
                <Input id="sp-reason" data-testid="settings-sp-reason" {...form.register('reason')} />
                {form.formState.errors.reason && (
                  <p className="text-xs text-destructive">{form.formState.errors.reason.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="sp-approval-no">审批单号（高风险必填）</Label>
                <Input
                  id="sp-approval-no"
                  data-testid="settings-sp-approval-no"
                  autoComplete="off"
                  {...form.register('approvalNo')}
                />
                {form.formState.errors.approvalNo && (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.approvalNo.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="sp-gradual-rollout">灰度范围说明（高风险必填）</Label>
                <Input
                  id="sp-gradual-rollout"
                  data-testid="settings-sp-gradual-rollout"
                  {...form.register('gradualRollout')}
                />
              </div>
              <div>
                <Label htmlFor="sp-impact-scope">影响范围说明（高风险必填）</Label>
                <Input
                  id="sp-impact-scope"
                  data-testid="settings-sp-impact-scope"
                  {...form.register('impactScope')}
                />
              </div>
              <div>
                <Label htmlFor="sp-rollback-plan">回滚方案（高风险必填）</Label>
                <Input
                  id="sp-rollback-plan"
                  data-testid="settings-sp-rollback-plan"
                  {...form.register('rollbackPlan')}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="submit"
                  data-testid="settings-sp-submit"
                  disabled={form.formState.isSubmitting}
                >
                  {form.formState.isSubmitting ? '提交中…' : '提交变更申请'}
                </Button>
                {submitMessage && (
                  <span className="text-xs text-emerald-600" data-testid="settings-sp-success">
                    {submitMessage}
                  </span>
                )}
              </div>
              {formError && (
                <p className="text-xs text-destructive" role="alert" data-testid="settings-sp-error">
                  {formError}
                </p>
              )}
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">变更申请记录</CardTitle>
          <CardDescription>
            包含当前 tenant/org 的待审、已审、已发布、已回滚记录。审批者可点击进行处置。
          </CardDescription>
        </CardHeader>
        <CardContent>
          {center.changes.loading && (
            <div className="text-sm text-muted-foreground" data-testid="settings-sp-changes-loading">
              正在加载…
            </div>
          )}
          {center.changes.error && (
            <div
              className="text-sm text-destructive"
              data-testid="settings-sp-changes-error"
              role="alert"
            >
              {center.changes.error}
            </div>
          )}
          {center.changes.data && center.changes.data.length === 0 && !center.changes.loading && (
            <div className="text-sm text-muted-foreground" data-testid="settings-sp-changes-empty">
              暂无变更申请。
            </div>
          )}
          {center.changes.data && center.changes.data.length > 0 && (
            <ul className="space-y-2" data-testid="settings-sp-changes-list">
              {center.changes.data.map((change) => (
                <li
                  key={change.id}
                  className="rounded border p-2 text-xs"
                  data-testid={`settings-sp-change-${change.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-mono">{change.parameterKey}</span>
                      <span className="ml-2 text-muted-foreground">
                        {formatJsonValue(change.beforeValue ?? null)} →{' '}
                        {formatJsonValue(change.afterValue)}
                      </span>
                    </div>
                    <span className="rounded bg-muted px-2 py-0.5">{change.status}</span>
                  </div>
                  <div className="mt-1 text-muted-foreground">
                    申请人：{change.requestedBy}；风险：{change.riskLevel}；原因：{change.reason}
                  </div>
                  {canApprove &&
                    (change.status === 'pending_approval' || change.status === 'approved') && (
                      <ApproveControls change={change} center={center} />
                    )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

interface ApproveControlsProps {
  readonly change: SystemParameterChangeDto;
  readonly center: ReturnType<typeof useSystemParameterCenter>;
}

function ApproveControls({ change, center }: ApproveControlsProps): ReactElement {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approvalNo, setApprovalNo] = useState(change.approvalNo ?? '');
  // No client-supplied "passed" string: the only second-factor field is the
  // opaque challenge id that the server verifies (Issue #267 audit finding #4).
  const [secondFactorChallengeId, setSecondFactorChallengeId] = useState('');
  const [comment, setComment] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const isHighRisk = change.riskLevel === 'high';
  const isFinalApproval = isHighRisk && change.status === 'approved';

  async function handleApprove(): Promise<void> {
    setError(null);
    if (isHighRisk && !secondFactorChallengeId.trim()) {
      setError('高风险审批必须先完成二次验证，并粘贴 challenge id。');
      return;
    }
    setBusy(true);
    try {
      await center.approveChange(change.id, {
        approvalNo: approvalNo || undefined,
        secondFactorChallengeId: secondFactorChallengeId.trim() || undefined,
        comment: comment || undefined,
      });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleReject(): Promise<void> {
    setError(null);
    if (rejectReason.length < 4) {
      setError('拒绝原因至少 4 个字符');
      return;
    }
    setBusy(true);
    try {
      await center.rejectChange(change.id, { reason: rejectReason });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-2 space-y-2 rounded border bg-muted/30 p-2">
      {isHighRisk && (
        <p
          className="text-xs text-muted-foreground"
          data-testid={`settings-sp-dual-review-hint-${change.id}`}
        >
          高风险参数采用双人复核：
          {isFinalApproval
            ? '当前等待第二位审批人发布，第二位必须与第一位不同。'
            : '当前等待第一位审批人；发布前还需要另一位审批人完成第二次审批。'}
          每位审批人必须独立完成二次验证并提供有效 challenge id。
        </p>
      )}
      <div className="grid gap-2 sm:grid-cols-3">
        <label className="text-xs">
          审批单号
          <Input
            value={approvalNo}
            onChange={(e) => setApprovalNo(e.target.value)}
            autoComplete="off"
            data-testid={`settings-sp-approval-no-${change.id}`}
          />
        </label>
        <label className="text-xs">
          二次验证 challenge id{isHighRisk ? '（高风险必填）' : '（可选）'}
          <Input
            value={secondFactorChallengeId}
            onChange={(e) => setSecondFactorChallengeId(e.target.value)}
            autoComplete="off"
            placeholder="服务端 face challenge id"
            data-testid={`settings-sp-second-factor-${change.id}`}
          />
        </label>
        <label className="text-xs">
          审批意见
          <Input
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            data-testid={`settings-sp-comment-${change.id}`}
          />
        </label>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          onClick={handleApprove}
          disabled={busy}
          data-testid={`settings-sp-approve-${change.id}`}
        >
          {busy
            ? '处理中…'
            : isFinalApproval
              ? '完成第二次审批并发布'
              : isHighRisk
                ? '完成第一次审批（待二审）'
                : '批准并发布'}
        </Button>
        <Input
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="拒绝原因 (>= 4 字符)"
          autoComplete="off"
          data-testid={`settings-sp-reject-reason-${change.id}`}
        />
        <Button
          size="sm"
          variant="outline"
          onClick={handleReject}
          disabled={busy}
          data-testid={`settings-sp-reject-${change.id}`}
        >
          拒绝
        </Button>
      </div>
      {error && (
        <p
          className="text-xs text-destructive"
          role="alert"
          data-testid={`settings-sp-approve-error-${change.id}`}
        >
          {error}
        </p>
      )}
    </div>
  );
}

function formatJsonValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * Best-effort text → typed value parser used by the change form.
 *
 * If the selected parameter is known, we coerce by `valueType`. Otherwise we
 * try JSON.parse and fall back to the raw string.
 */
type JsonValue =
  | string
  | number
  | boolean
  | readonly JsonValue[]
  | { readonly [key: string]: JsonValue };

function parseTextValue(
  text: string,
  selected: SystemParameterDto | undefined,
): JsonValue {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    throw new Error('新值必填');
  }
  const valueType = selected?.valueType;
  switch (valueType) {
    case 'boolean': {
      if (trimmed === 'true') return true;
      if (trimmed === 'false') return false;
      throw new Error('布尔类型的新值必须是 true 或 false');
    }
    case 'integer': {
      const n = Number(trimmed);
      if (!Number.isInteger(n)) {
        throw new Error('整型参数的新值必须是整数');
      }
      return n;
    }
    case 'decimal': {
      const n = Number(trimmed);
      if (!Number.isFinite(n)) {
        throw new Error('小数参数的新值必须是有限数字');
      }
      return n;
    }
    case 'string':
      try {
        const parsed = JSON.parse(trimmed);
        if (typeof parsed === 'string') return parsed;
        return trimmed;
      } catch {
        return trimmed;
      }
    case 'json':
    case 'enum':
    default:
      try {
        return JSON.parse(trimmed);
      } catch {
        return trimmed;
      }
  }
}
